/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  DatePicker,
  Modal,
  notification,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { IBusinessUnit } from '@/interfaces/business-unit';

import { formatNumber } from '@/utils/dataUtils';
import { IMediaUpload } from '@/interfaces/media';
import { uploadMedia } from '@/adapters/media';
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/th_TH';
import {
  IFinancialMatchingReportRes,
  IImportFinancialMatchingReportReq,
} from '@/interfaces/report';
import { isEmpty, isUndefined } from 'lodash';
import _ from 'lodash';
import { importFinancialMatching } from '@/adapters/report';

interface IProps {
  bus: IBusinessUnit[];
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const ImportFinancialMatchingModal = ({
  open,
  handleOk,
  handleCancel,
}: IProps) => {
  const [month, setMonth] = useState<string>(
    dayjs().startOf('month').format('YYYY-MM-DD'),
  );
  // const [bu, setBu] = useState<number>(0);
  const [order, setOrder] = useState<Partial<IFinancialMatchingReportRes>[]>(
    [],
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const columns: TableProps<Partial<IFinancialMatchingReportRes>>['columns'] = [
    {
      title: 'OrderNo',
      dataIndex: 'keminOrderNo',
      key: 'keminOrderNo',
      className: 'font-bold',
      width: 130,
      align: 'center',
    },
    {
      title: 'Customer Code',
      dataIndex: 'customerCode',
      key: 'customerCode',
      className: 'font-bold',
      width: 130,
      align: 'center',
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
    },
    {
      title: 'InvoicedNo',
      dataIndex: 'keminInvoiceNo',
      key: 'keminInvoiceNo',
      className: 'font-bold',
      width: 130,
      align: 'center',
    },
    {
      title: 'LineAmt',
      dataIndex: 'keminLineAmount',
      key: 'keminLineAmount',
      className: 'font-bold',
      width: 130,
      align: 'right',
      render: _ => formatNumber(_ || 0, 2),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setOrder([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelData(file)) as any[];
      excelData = excelData
        .slice(4)
        .filter(c => !isEmpty(c[0]) || !isUndefined(c[0]))
        .map((c: any) => {
          const match: IFinancialMatchingReportRes = {
            keminOrderNo: (c[10] as string).toString(),
            keminInvoiceNo: (c[15] as string).toString(),
            customerCode: (c[3] as string).toString(),
            customerName: (c[4] as string).toString(),
            keminInvoiceDate: dayjs('1899-12-30')
              .add(c[16], 'day')
              .format('YYYY-MM-DD'),
            keminLineAmount: c[37],
          } as IFinancialMatchingReportRes;
          return match;
        });
      const groupedData = _(excelData)
        .groupBy('keminOrderNo')
        .map((items, key) => ({
          keminOrderNo: key,
          keminLineAmount: _.sumBy(items, 'keminLineAmount'),
          keminInvoiceNo: items[0].keminInvoiceNo, // Get the first invoice number
          keminOrderCreateDate: items[0].keminInvoiceDate,
          customerCode: items[0].customerCode,
          customerName: items[0].customerName,
        }))
        .value();
      setOrder(groupedData);
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const upLoadmutations = useMutation({
    mutationFn: async (data: Partial<IMediaUpload>) => await uploadMedia(data),
    mutationKey: ['upload-media'],
    onSuccess: data => {
      console.log(data);
    },
    onError: error => {
      console.log(error);
    },
  });

  const mutations = useMutation({
    mutationFn: async (data: IImportFinancialMatchingReportReq) =>
      await importFinancialMatching(data),
    mutationKey: ['import-financial-matching'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        // setBu(0);
        setMonth(dayjs().startOf('month').format('YYYY-MM-DD'));
        notification.success({
          message: 'นำเข้า Financial Matching สำเร็จ',
        });
        const file: Partial<IMediaUpload> = {
          file: fileList.length ? (fileList[0] as any) : null,
          modelId: 1,
          modelType: 'financial',
        };
        upLoadmutations.mutateAsync(file);
        handleOk();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    const dataImport: IImportFinancialMatchingReportReq = {
      data: order,
      createDate: month,
    };
    mutations.mutateAsync(dataImport);
  };

  const onRangeChange = (dates: null | (Dayjs | null)) => {
    if (dates) {
      const startDate = dayjs(dates).startOf('month').format('YYYY-MM-DD');
      setMonth(startDate);
    }
  };

  const isDisabled = !!month && !!order?.length;

  return (
    <Modal
      title="นำเข้า Financial Report"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          disabled={!isDisabled}
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
      afterClose={() => {
        setFileList([]);
        setOrder([]);
        // setBu(0);
        setMonth(dayjs().startOf('month').format('YYYY-MM-DD'));
      }}
    >
      <div className="flex flex-col gap-2 w-full">
        <div className="flex flex-row gap-2 justify-between items-center">
          <Upload {...props}>
            {fileList.length === 0 && (
              <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
            )}
          </Upload>
          <div className="flex flex-row gap-2">
            {/* <BusinessUnitSelector
              handleSelectData={function (id: number): void {
                setBu(id);
              }}
            /> */}
            <DatePicker
              picker="month"
              size="large"
              locale={locale}
              defaultValue={dayjs()}
              onChange={onRangeChange}
              allowClear={false}
              className="min-w-[150px]"
            />
          </div>
        </div>
        <TableComponent<Partial<IFinancialMatchingReportRes>>
          columns={columns}
          criteria={{}}
          data={{
            data: order,
            totalRecords: order.length,
          }}
          loading={false}
          onTableChange={undefined}
          scroll={false}
          rowKey="keminOrderNo"
          defaultPagination={true}
        />
      </div>
    </Modal>
  );
};

export default ImportFinancialMatchingModal;
